import type { GridCustomRowProps } from "@tql/ui-components-react/kendo/grid";
import { Skeleton } from "@tql/ui-components-react/tql/indicators";

import type { Load } from "~/shared/schemas/loads-schema.ts";

/** Sentinel value used in `loadStatus` to mark a row as a skeleton placeholder. */
export const SKELETON_LOAD_STATUS = "__skeleton_row__";

const SKELETON_ROW_COUNT = 6;

const makeSkeletonSentinel = (): Load => ({
	poNumber: null,
	pickupDate: null,
	customerName: null,
	customerId: null,
	customerPO: null,
	carrierName: null,
	deliveryDate: null,
	isDropsDelivered: null,
	isLoaded: null,
	lane: null,
	trailerType: null,
	temperature: null,
	weight: null,
	pickupPOList: null,
	firstPickName: null,
	firstPickCity: null,
	firstPickState: null,
	firstPickZIP: null,
	deliveryPOList: null,
	lastDropName: null,
	lastDropCity: null,
	lastDropState: null,
	lastDropZIP: null,
	lastDropDeliveryPO: null,
	lastDropDeliveryDate: null,
	loadStatus: SKELETON_LOAD_STATUS,
	proNumber: null,
	shipmentTypeId: null,
	quoteId: null,
	mgLoadId: null,
	custRefNum: null,
	soNumber: null,
	xaShipmentId: null,
	minFreightDBChgsId: null,
	minFreightDBChangeUTCDT: null,
	maxFreightDBChgsId: null,
	maxFreightDBChangeUTCDT: null,
	minMoveDBChangeUTCDT: null,
	maxMoveDBChangeUTCDT: null,
	brokerId: null,
	loadStatusInfo: null,
	rowLastUpdatedUtc: "",
	isVoided: null,
	isDispatched: null,
	isNeedsAttention: null,
});

/**
 * Frozen array of skeleton sentinel rows appended to the grid data while a
 * "next page" fetch is in flight.
 */
export const SKELETON_ROWS: readonly Load[] = Object.freeze(
	Array.from({ length: SKELETON_ROW_COUNT }, () => makeSkeletonSentinel()),
);

/**
 * Builds a Kendo Grid custom row renderer that swaps in an animated skeleton
 * `<tr>` for any data item whose `loadStatus` matches `SKELETON_LOAD_STATUS`.
 *
 * @param visibleColumnCount - Number of currently visible columns; controls how
 *   many `<td>` cells the skeleton row spans.
 */
export const createSkeletonAwareRow = (visibleColumnCount: number) => {
	return function SkeletonAwareRow(props: GridCustomRowProps) {
		const dataItem = props.dataItem as Load | undefined;
		if (dataItem?.loadStatus === SKELETON_LOAD_STATUS) {
			return (
				<tr
					aria-label={"Loading more shipments"}
					className={"loads-skeleton-row"}
					role={"row"}
				>
					{Array.from({ length: visibleColumnCount }, (_, index) => (
						<td key={index}>
							<div className={"loads-skeleton-cell"}>
								<Skeleton animation={{ type: "wave" }} />
							</div>
						</td>
					))}
				</tr>
			);
		}
		return <tr {...props.trProps}>{props.children}</tr>;
	};
};
